/**
 * EngagementTimeline (#151) — chronological list of what the AI proposed
 * during a conversation and how the user received it.
 *
 * Renders inside AdminConversationDetail. Fetches events via
 * adminApi.getConversationEvents(convId). Quiet on empty (older
 * conversations recorded before #151 shipped have no events).
 */
import React, { useEffect, useState } from 'react';
import { adminApi } from './adminApi';

// Icon per event_type — kept minimal so the timeline reads like a story.
const EVENT_ICONS = {
  call_initiated: '📞',
  call_ended: '📵',
  proposal_made: '🎯',
  proposal_response: '↳',
  proposal_refused: '🚫',
  token_breakdown: '📊',
  activity_started: '▶️',
  activity_ended: '⏹️',
  callback_requested: '🔁',
  callback_resolved: '✓',
  reengagement_attempt: '👋',
  mic_warning_shown: '🤐',
  user_request: '🙋',
  empty_turn: '…',
  topic_used: '💬',
  admin_ai_run: '🤖',
};

function formatTime(iso) {
  if (!iso) return '';
  // #716 follow-up — UTC matches the server log timestamps; admin no
  // longer needs to mentally subtract two hours when correlating a
  // proposal_made event with the matching `Streamed transcription`
  // line in the app logs.
  const d = new Date(iso);
  const h = String(d.getUTCHours()).padStart(2, '0');
  const m = String(d.getUTCMinutes()).padStart(2, '0');
  const s = String(d.getUTCSeconds()).padStart(2, '0');
  return `${h}:${m}:${s}Z`;
}

function describeEvent(evt) {
  const p = evt.payload || {};
  switch (evt.event_type) {
    case 'call_initiated': {
      const ct = p.call_type || 'normal';
      if (ct === 'normal') return 'User called in';
      return `Companion-initiated call (${ct})`;
    }
    case 'call_ended':
      return `Call ended (${p.end_reason || 'unknown'}, ${p.turn_count ?? '?'} turns)`;
    case 'proposal_made': {
      const t = p.proposal_type || 'topic';
      const s = p.specification ? `: ${p.specification}` : '';
      const trig = p.trigger ? ` [${p.trigger}]` : '';
      return `Proposed ${t}${s}${trig}`;
    }
    case 'proposal_response':
      return `User ${p.response || 'responded'}`;
    case 'token_breakdown': {
      // #345 — per-turn token spend. Headline: cache hit / total /
      // output. Detailed kv values still render via the generic
      // payload row beneath; just give the timeline a readable summary.
      const cr = Number(p.cache_read_input_tokens || 0);
      const cc = Number(p.cache_creation_input_tokens || 0);
      const inp = Number(p.input_tokens || 0);
      const out = Number(p.output_tokens || 0);
      const cacheTag = cr > 0 ? `cache hit ${cr}` : (cc > 0 ? `cache write ${cc}` : 'no cache');
      return `Tokens: ${cacheTag} · input ${inp} · output ${out}`;
    }
    case 'proposal_refused': {
      // #340 — server-side content-safety filter dropped a media tag.
      // Friendly reason labels so admin doesn't have to memorise
      // 'classifier_borderline' / 'keyword_block:*' / 'classifier_unsafe'.
      const reasonRaw = p.reason || 'unknown';
      let reasonLabel;
      if (reasonRaw === 'classifier_unsafe') reasonLabel = 'AI classifier: unsafe';
      else if (reasonRaw === 'classifier_borderline') reasonLabel = 'AI classifier: borderline';
      else if (reasonRaw.startsWith('keyword_block:')) {
        reasonLabel = `Blocked phrase: "${reasonRaw.slice('keyword_block:'.length)}"`;
      } else {
        reasonLabel = reasonRaw;
      }
      const q = p.query ? `: "${p.query}"` : '';
      return `Safety filter blocked${q} (${reasonLabel})`;
    }
    case 'activity_started':
      return `Activity started: ${p.specification || p.proposal_type || 'unknown'}`;
    case 'activity_ended':
      if (p.duration_s) return `Activity ended (${p.duration_s}s)`;
      return `Activity ended: ${p.specification_kind || p.specification || ''}`;
    case 'callback_requested':
      return `User asked Berta to recall: "${p.target_text || ''}"`;
    case 'callback_resolved':
      return p.success ? 'Berta successfully recalled' : 'Recall attempt — outcome unclear';
    case 'reengagement_attempt':
      return `Re-engagement attempt (ladder step ${p.ladder_step ?? '?'})`;
    case 'mic_warning_shown':
      return 'Mic-warning shown ("having trouble hearing you")';
    case 'user_request':
      return `User requested: ${p.request_type || p.request_text || 'something'}`;
    case 'empty_turn':
      return 'Empty turn (no speech)';
    case 'topic_used':
      return `Topic referenced: ${p.topic_source || ''}${p.engaged === false ? ' (no engagement)' : ''}`;
    case 'admin_ai_run': {
      // #168 PR-3 — render each post-process with a friendly label,
      // aggregated count across the call, and a short item peek.
      const TASK_LABELS = {
        memory_extraction:   'Memory extraction',
        safeguarding:        'Safeguarding check',
        reminder_detect:     'Reminder detection',
        blocked_time_detect: 'Blocked-time detection',
        personality_adjust:  'Personality adjustment',
        summarise:           'Summary written',
        extract_interests:   'Interest detection',
      };
      const label = TASK_LABELS[p.task] || `Admin AI: ${p.task || 'unknown'}`;
      const items = Array.isArray(p.items) && p.items.length
        ? ` — ${p.items.slice(0, 3).join(', ')}${p.items.length > 3 ? '…' : ''}`
        : '';
      // Show "ran clean" for tasks that did nothing (count=0) but
      // executed — useful for "did this even run?" debugging.
      if (typeof p.count === 'number' && p.count === 0) {
        const turns = p.turns_run ? ` (${p.turns_run} turn${p.turns_run === 1 ? '' : 's'})` : '';
        return `${label}: nothing new${turns}`;
      }
      const count = typeof p.count === 'number' ? ` · ${p.count}` : '';
      return `${label}${count}${items}`;
    }
    default:
      return evt.event_type;
  }
}

export default function EngagementTimeline({ convId }) {
  const [events, setEvents] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!convId) return;
    adminApi.getConversationEvents(convId)
      .then(d => setEvents(d.events || []))
      .catch(e => setError(e.message));
  }, [convId]);

  return (
    <div style={styles.container}>
      <div style={styles.title}>Engagement Timeline (Internal — #151)</div>
      {error && <div style={styles.error}>Error: {error}</div>}
      {events === null && !error && <div style={styles.muted}>Loading…</div>}
      {events !== null && events.length === 0 && (
        <div style={styles.muted}>No events recorded for this conversation.</div>
      )}
      {events && events.length > 0 && (
        <div style={styles.list}>
          {events.map(evt => (
            <div key={evt.id} style={styles.row}>
              <span style={styles.time}>{formatTime(evt.occurred_at)}</span>
              <span style={styles.icon}>{EVENT_ICONS[evt.event_type] || '·'}</span>
              <div style={styles.body}>
                <div style={styles.summary}>{describeEvent(evt)}</div>
                {evt.payload && Object.keys(evt.payload).length > 0 && (
                  <div style={styles.payload}>
                    {Object.entries(evt.payload).map(([k, v]) => (
                      <span key={k} style={styles.kv}>
                        <span style={styles.k}>{k}</span>
                        <span>: </span>
                        <span style={styles.v}>{typeof v === 'object' ? JSON.stringify(v) : String(v)}</span>
                      </span>
                    ))}
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

const styles = {
  container: {
    background: '#fff',
    borderRadius: 12,
    boxShadow: '0 2px 12px rgba(0,0,0,0.08)',
    padding: '20px 24px',
    marginTop: 20,
  },
  title: {
    fontSize: '0.9rem',
    fontWeight: 700,
    color: '#2C2C2C',
    marginBottom: 14,
    letterSpacing: '-0.01em',
  },
  list: { display: 'flex', flexDirection: 'column', gap: 10 },
  row: {
    display: 'grid',
    gridTemplateColumns: '90px 24px 1fr',
    alignItems: 'baseline',
    gap: 8,
    fontSize: '0.85rem',
    paddingBottom: 8,
    borderBottom: '1px dashed #f0ece6',
  },
  time: { color: '#888', fontFamily: 'Menlo, monospace', fontSize: '0.78rem' },
  icon: { fontSize: '1rem', textAlign: 'center' },
  body: {},
  summary: { color: '#2C2C2C', fontWeight: 500 },
  payload: { color: '#777', fontSize: '0.78rem', marginTop: 3, display: 'flex', flexWrap: 'wrap', gap: '8px 14px' },
  kv: { whiteSpace: 'nowrap' },
  k: { color: '#aaa', fontWeight: 500 },
  v: { color: '#555' },
  error: { color: '#C0392B', fontSize: '0.85rem' },
  muted: { color: '#aaa', fontSize: '0.85rem', fontStyle: 'italic' },
};
